import { Check, ChevronRight, X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import useTranslation from "../../client/hooks/useTranslation";
import { useLiteData } from "../data/LiteDataProvider";
import { useLiteActions } from "../shell/LiteActions";
import { trackOnboardingChecklistHidden, trackOnboardingCtaClicked } from "../../client/utils/posthog";
import { Bar, cardCls } from "../ui/ui";

interface Step { id: string; title: string; desc: string; done: boolean; cta: string; onClick?: () => void; to?: string }

/** Checklist de démarrage : visible tant que l'essentiel n'est pas fait. */
export function GettingStarted() {
  const t = useTranslation().lite.onboarding.checklist;
  const { data: user } = useAuth();
  const { accounts, trades, loading } = useLiteData();
  const { openAddAccount, openAddTrade } = useLiteActions();
  const key = `edgely:lite:checklistHidden:${user?.id ?? "anon"}`;
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(key) === "1"; } catch { return false; } });

  const steps: Step[] = [
    { id: "account", title: t.account, desc: t.accountDesc, done: accounts.length > 0, cta: t.accountCta, onClick: openAddAccount },
    { id: "trades", title: t.trades, desc: t.tradesDesc, done: trades.length > 0, cta: t.tradesCta, onClick: openAddTrade },
    { id: "note", title: t.note, desc: t.noteDesc, done: trades.some(x => x.note || x.tags.length), cta: t.noteCta, to: "/trading-journal" },
  ];
  const done = steps.filter(s => s.done).length;
  if (loading || hidden || done === steps.length) return null;

  const hide = () => { trackOnboardingChecklistHidden(done, steps.length); setHidden(true); try { localStorage.setItem(key, "1"); } catch { /* ignore */ } };

  return (
    <section className={`${cardCls} p-5`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-semibold">{t.title}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">{t.progress(done, steps.length)}</p>
        </div>
        <button type="button" className="w-8 h-8 grid place-items-center rounded-lg hover:bg-muted text-muted-foreground" onClick={hide} aria-label={t.hide}><X size={16} /></button>
      </div>
      <Bar value={done / steps.length} className="mt-3" />
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        {steps.map(s => {
          const cls = `flex items-start gap-3 rounded-xl border border-border p-3.5 text-left transition-colors ${s.done ? "opacity-60" : "hover:border-primary/40"}`;
          const body = (
            <>
              <span className={`w-6 h-6 rounded-full grid place-items-center shrink-0 ${s.done ? "bg-pnlPositive/20 text-pnlPositive" : "border border-border"}`}>{s.done && <Check size={14} />}</span>
              <span className="flex-1 min-w-0">
                <span className={`block text-sm font-medium ${s.done ? "line-through" : ""}`}>{s.title}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{s.desc}</span>
                {!s.done && <span className="inline-flex items-center gap-0.5 text-xs text-primary mt-2">{s.cta} <ChevronRight size={12} /></span>}
              </span>
            </>
          );
          if (s.done) return <div key={s.id} className={cls}>{body}</div>;
          const track = () => trackOnboardingCtaClicked("lite", `checklist_${s.id}`);
          return s.to
            ? <Link key={s.id} to={s.to} onClick={track} className={cls}>{body}</Link>
            : <button type="button" key={s.id} onClick={() => { track(); s.onClick?.(); }} className={cls}>{body}</button>;
        })}
      </div>
    </section>
  );
}
